import React from 'react';
import { CheckCircle, Warning, Circle, Clock, User, CalendarBlank, Target } from '@phosphor-icons/react';

interface GoalCardItem {
    title?: string;
    employee_name?: string;
    employeeName?: string;
    status?: string;
    priority?: string;
    progress?: number;
    updated_at?: string;
    updatedAt?: string;
    goal_level?: string;
    goalLevel?: string;
    category_name?: string;
    categoryName?: string;
}

interface GoalCardProps {
    data: GoalCardItem[] | { data: GoalCardItem[]; metadata?: any; meta?: any; schema?: any };
    options?: {
        showProgress?: boolean;
        showOwner?: boolean;
        showDueDate?: boolean;
        showPriority?: boolean;
        showStatus?: boolean;
        maxCards?: number;
    };
}

const getStatusConfig = (status: string) => {
    const s = (status || '').toLowerCase();
    if (s.includes('completed') || s.includes('done') || s.includes('achieved')) return { color: 'text-green-600 bg-green-50 border border-green-100', icon: CheckCircle, label: 'Completed' };
    if (s.includes('risk') || s.includes('behind')) return { color: 'text-red-600 bg-red-50 border border-red-100', icon: Warning, label: 'At Risk' };
    if (s.includes('progress') || s.includes('active') || s.includes('on track')) return { color: 'text-blue-600 bg-blue-50 border border-blue-100', icon: Circle, label: 'In Progress' };
    if (s.includes('not started') || s.includes('pending') || s.includes('new')) return { color: 'text-gray-600 bg-gray-50 border border-gray-100', icon: Clock, label: 'Not Started' };
    return { color: 'text-gray-600 bg-gray-50 border border-gray-100', icon: Clock, label: status || 'Unknown' };
};

const getPriorityColor = (priority: string) => {
    const p = (priority || '').toLowerCase();
    if (p === 'high' || p === 'critical') return 'text-red-600 bg-red-50 border border-red-100';
    if (p === 'medium') return 'text-orange-600 bg-orange-50 border border-orange-100';
    return 'text-blue-600 bg-blue-50 border border-blue-100';
};

const formatDate = (dateStr?: string | null): string | null => {
    if (!dateStr) return null;
    try {
        const date = new Date(dateStr);
        if (isNaN(date.getTime())) return null;
        return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    } catch {
        return null;
    }
};

export const GoalCard: React.FC<GoalCardProps> = ({ data, options = {} }) => {
    const {
        showProgress = true,
        showOwner = true,
        showDueDate = true,
        showPriority = true,
        showStatus = true,
        maxCards = 10,
    } = options;

    let items: GoalCardItem[] = [];
    if (Array.isArray(data)) {
        items = data;
    } else if (data && typeof data === 'object' && 'data' in data && Array.isArray(data.data)) {
        items = data.data;
    }

    const displayItems = items.slice(0, maxCards);

    if (!displayItems || displayItems.length === 0) {
        return (
            <div className="flex flex-col items-center justify-center h-full text-gray-400 p-6">
                <div className="w-14 h-14 rounded-full bg-gray-100 flex items-center justify-center mb-3">
                    <Target size={28} className="text-gray-400" />
                </div>
                <span className="text-sm font-medium">No goals found</span>
            </div>
        );
    }

    return (
        <div className="flex flex-col gap-2.5 p-3 h-full overflow-auto">
            {displayItems.map((item, index) => {
                const title = item.title || 'Untitled Goal';
                const owner = item.employeeName || item.employee_name || '';
                const status = item.status || '';
                const priority = item.priority || '';
                const progress = item.progress;
                const updatedAt = formatDate(item.updatedAt || item.updated_at);
                const category = item.categoryName || item.category_name || '';

                const statusConfig = getStatusConfig(status);
                const StatusIcon = statusConfig.icon;

                return (
                    <div
                        key={`goal-${index}`}
                        className="group bg-white rounded-lg border border-gray-200 p-3.5 hover:border-blue-300 hover:shadow-sm transition-all cursor-pointer"
                    >
                        {/* Row 1: Title + Priority */}
                        <div className="flex items-start justify-between gap-3 mb-2.5">
                            <h3 className="text-sm font-semibold text-gray-900 leading-snug line-clamp-2 group-hover:text-blue-600 transition-colors flex-1">
                                {title}
                            </h3>
                            {showPriority && priority && (
                                <span className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wide flex-shrink-0 ${getPriorityColor(priority)}`}>
                                    {priority}
                                </span>
                            )}
                        </div>

                        {/* Row 2: Status + Category badges */}
                        <div className="flex items-center gap-2 flex-wrap mb-3">
                            {showStatus && status && (
                                <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wide ${statusConfig.color}`}>
                                    <StatusIcon size={10} weight="fill" />
                                    {statusConfig.label}
                                </span>
                            )}
                            {category && (
                                <span className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-medium bg-gray-100 text-gray-500">
                                    {category}
                                </span>
                            )}
                        </div>

                        {/* Row 3: Progress Bar */}
                        {showProgress && progress !== undefined && progress !== null && (
                            <div className="flex items-center gap-3 mb-3">
                                <div className="flex-1 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                                    <div
                                        className="h-full bg-blue-500 rounded-full transition-all duration-500"
                                        style={{ width: `${Math.min(progress, 100)}%` }}
                                    />
                                </div>
                                <span className="text-xs font-medium text-gray-600 min-w-[32px] text-right">
                                    {Math.round(progress)}%
                                </span>
                            </div>
                        )}

                        {/* Row 4: Footer */}
                        <div className="flex items-center justify-between text-xs text-gray-500 pt-2.5 border-t border-gray-100">
                            {showOwner && owner ? (
                                <div className="flex items-center gap-1.5 min-w-0">
                                    <User size={14} className="text-gray-400 flex-shrink-0" />
                                    <span className="truncate">{owner}</span>
                                </div>
                            ) : (
                                <div />
                            )}
                            {showDueDate && updatedAt && (
                                <div className="flex items-center gap-1.5 flex-shrink-0">
                                    <CalendarBlank size={14} className="text-gray-400" />
                                    <span>{updatedAt}</span>
                                </div>
                            )}
                        </div>
                    </div>
                );
            })}
        </div>
    );
};
